// Precondition: None
// Postcondition: Renders a signup CTA banner with customizable heading, subtext, and button
// Invariant: All text configurable via attributes with sensible defaults

import "../button/button.js";

class SignupCta extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return ["heading", "subtext", "button-text", "button-href"];
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    if (this.shadowRoot && this.shadowRoot.children.length > 0) {
      this.render();
    }
  }

  private render(): void {
    const heading =
      this.getAttribute("heading") || "Unlock the full picture in 30 seconds.";
    const subtext =
      this.getAttribute("subtext") || "No credit card. No trial period. Just data.";
    const buttonText = this.getAttribute("button-text") || "Create Free Account";
    const defaultLoginUrl = `https://auth.formthirteen.com/login?redirect_uri=${encodeURIComponent(window.location.href)}`;
    const buttonHref = this.getAttribute("button-href") || defaultLoginUrl;

    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate(heading, subtext, buttonText, buttonHref);

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
      }

      .cta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
        padding: 20px 24px;
        background: var(--surface);
        border: 1px solid var(--card-border);
        border-radius: var(--radius-md, 12px);
      }

      .cta-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .cta-heading {
        font-family: var(--font-display, "Space Grotesk", system-ui, sans-serif);
        font-size: 1rem;
        font-weight: 600;
        color: var(--heading);
        margin: 0;
        line-height: 1.3;
      }

      .cta-subtext {
        font-size: 0.875rem;
        color: var(--muted);
        margin: 0;
        line-height: 1.4;
      }

      .cta-action {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-shrink: 0;
      }

      .cta-free {
        font-size: 0.75rem;
        color: var(--muted);
        white-space: nowrap;
      }

      @media (max-width: 600px) {
        .cta {
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
        }

        .cta-action {
          width: 100%;
        }

        ft-button {
          flex: 1;
        }
      }
    `;
  }

  private getTemplate(
    heading: string,
    subtext: string,
    buttonText: string,
    buttonHref: string
  ): string {
    return `
      <div class="cta">
        <div class="cta-content">
          <p class="cta-heading">${heading}</p>
          <p class="cta-subtext">${subtext}</p>
        </div>
        <div class="cta-action">
          <ft-button href="${buttonHref}">${buttonText}</ft-button>
          <span class="cta-free">Free</span>
        </div>
      </div>
    `;
  }
}

// Guard against duplicate registration
if (!customElements.get("ft-signup-cta")) {
  customElements.define("ft-signup-cta", SignupCta);
}
